import React, { useState, useContext, useEffect, useCallback } from 'react'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import queryString from 'query-string'
import classNames from 'classnames'
import { flowRight, get } from 'lodash'
import moment from 'moment'
import { Trans } from 'react-i18next'
import { withTranslationCustom } from 'lib/i18next'

import Button from 'components/UI/Button'
import { EventFormLogicContext } from 'components/Events/EventForm/EventFormLogic'
import VehicleProfileLogic from 'components/Vehicles/VehicleProfile/VehicleProfileLogic'
import AppContext from 'components/AppContext'
import EventLogic from 'components/Events/EventLogic'

import './inspection-event-form-reminder-trafi-dialog.pcss'

const InspectionEventFormReminderTrafiDialog = ({ t, location, wrappedVehicle, children }) => {
    const query = queryString.parse(location.search)

    const { writeEventFormState, writeEventFormInitialEventValues } = useContext(EventFormLogicContext)

    const trafiData = wrappedVehicle.getTrafiData()
    const lastTraficomFetchDate = wrappedVehicle.getLastTrafiDataFetchDate()
    const lastTraficomFetchDateFormatted = wrappedVehicle.getLastTrafiDataFetchDate('DD.MM.YYYY HH:mm')
    const trafiDataFetchedToday = lastTraficomFetchDate && moment(lastTraficomFetchDate).isSame(moment(Date.now()), 'day')
    const nextInspectionDate = wrappedVehicle.getVehicleInspectionValidUntil()

    const [useTrafiDataParam, setUseTrafiDataParam] = useState(query['use-trafi-data'] !== undefined || false)
    const [displayTrafiDialog, setDisplayTrafiDialog] = useState(true)
    const [insufficientDataError, setInsufficientDataError] = useState(false)
    const [allowFetch, setAllowFetch] = useState(!nextInspectionDate || !trafiDataFetchedToday)

    const displayFetchButton = allowFetch && !nextInspectionDate
    const displayRefreshButton = allowFetch && !!nextInspectionDate

    const setupReminderTrafiData = useCallback(() => {
        writeEventFormInitialEventValues({ eventTime: nextInspectionDate })
        writeEventFormState({ trafiData: trafiData })
    }, [nextInspectionDate, trafiData, writeEventFormInitialEventValues, writeEventFormState])

    useEffect(() => {
        if (useTrafiDataParam) {
            setupReminderTrafiData()
            setDisplayTrafiDialog(false)
            setUseTrafiDataParam(false)
        }
    }, [useTrafiDataParam, setupReminderTrafiData, nextInspectionDate])

    if (useTrafiDataParam) {
        return null
    }

    if (!displayTrafiDialog) {
        return children()
    }

    const handleFetchData = (requestTrafiData) => {
        return async () => {
            await requestTrafiData(wrappedVehicle.getId()).then((response) => {
                const nextInspectionDate = get(response, 'data.requestTrafiData.trafiData.vehicleDateOfInspectionValidUntil', null)
                setAllowFetch(false)
                if (!nextInspectionDate) {
                    setInsufficientDataError(true)
                }
            }).catch(() => {
                setAllowFetch(false)
            })
        }
    }

    const renderWithoutTrafiData = () => {
        return (
            <div className="inspection-event-form-reminder-trafi-dialog__trafi-description">
                <p>
                    {t('events:inspectionDialog.createReminder.withoutTrafiData.description')}
                </p>
            </div>
        )
    }

    const renderWithTrafiData = (vehicleProfileLogic) => {
        const { requestTrafiData } = vehicleProfileLogic
        const status = wrappedVehicle.getVehicleInspectionStatus(t)

        return (
            <>
                <p>
                    {`${t('events:inspectionDialog.createReminder.withTrafiData.nextInspectionDate')}: `}
                    <span className={classNames('inspection-event-form-reminder-trafi-dialog__inspection-status nowrap')}>
                        {status.valid ? (
                            <b>{status.dateFormatted}</b>
                        ) : (
                            <b className="alert-text">{`${status.dateFormatted} ${t('events:inspectionDialog.inspectionExpired')}`}</b>
                        )}
                    </span>
                </p>
                <div className="inspection-event-form-reminder-trafi-dialog__trafi-description">
                    <p>
                        <Trans i18nKey="events:inspectionDialog.createReminder.withTrafiData.description" lastTraficomFetchDateFormatted={lastTraficomFetchDateFormatted}>
                            description <b className="nowrap">{{ lastTraficomFetchDateFormatted }}</b>
                        </Trans>
                        {' '}
                        {displayRefreshButton && (
                            <Button
                                onClick={handleFetchData(requestTrafiData)}
                                trackName="refreshTrafiData"
                                className="inline-button nowrap"
                            >
                                {t('events:inspectionDialog.createReminder.actions.refreshTrafiData')}
                            </Button>
                        )}
                    </p>

                    <div className="inspection-event-form-reminder-trafi-dialog__text">
                        {t('events:inspectionDialog.createReminder.withTrafiData.traficomText')}
                    </div>
                </div>
            </>
        )
    }

    const renderFetchButton = (vehicleProfileLogic) => {
        const { requestTrafiData, loading } = vehicleProfileLogic
        return (
            <Button
                onClick={handleFetchData(requestTrafiData)}
                className="button button--secondary"
                trackName="fetchTrafiData"
                loading={loading}
            >
                {t('events:inspectionDialog.createReminder.actions.fetchTrafiData')}
            </Button>
        )
    }

    const renderUseDataButton = (eventLogic) => {
        const { createLatestInspectionEventFromTrafiData } = eventLogic
        return (
            <Button
                onClick={async () => {
                    await createLatestInspectionEventFromTrafiData(wrappedVehicle.getId())
                        .then((result) => {
                            if (result.data.createLatestInspectionEventFromTrafiData) {
                                writeEventFormState({ latestEventCreated: true })
                            }
                            setupReminderTrafiData()
                            setDisplayTrafiDialog(false)
                        })
                        .catch(() => {
                            setupReminderTrafiData()
                            setDisplayTrafiDialog(false)
                        })
                }}
                className="button button--secondary"
                trackName="useTrafiData"
            >
                {t('events:inspectionDialog.createReminder.actions.useTrafiData')}
            </Button>
        )
    }

    const renderSkipButton = () => {
        return (
            <Button
                onClick={() => setDisplayTrafiDialog(false)}
                className="button button--tertiary"
                trackName="setManually"
            >
                {t('events:inspectionDialog.createReminder.actions.skip')}
            </Button>
        )
    }

    const renderContinueOnErrorButton = () => {
        return (
            <Button
                onClick={() => setDisplayTrafiDialog(false)}
                className="button"
                trackName="onErrorContinue"
            >
                {t('common:continue')}
            </Button>
        )
    }

    const renderActions = (vehicleProfileLogic, eventLogic) => {
        const { error } = vehicleProfileLogic

        if (error || insufficientDataError) {
            return renderContinueOnErrorButton()
        }

        const displayUseTrafiDataButton = !!nextInspectionDate

        return (
            <>
                {displayFetchButton && renderFetchButton(vehicleProfileLogic)}
                {displayUseTrafiDataButton && renderUseDataButton(eventLogic)}
                {!displayUseTrafiDataButton && renderSkipButton()}
            </>
        )
    }

    return (
        <AppContext context="createReminderTrafiDialog">
            <EventLogic>
                {(eventLogic) => (
                    <VehicleProfileLogic>
                        {(vehicleProfileLogic) => (
                            <div className={classNames('inspection-event-form-reminder-trafi-dialog', { loading: (vehicleProfileLogic.loading || eventLogic.loading) })}>
                                <h2>{t('events:inspectionDialog.createReminder.title')}</h2>
                                {nextInspectionDate ? renderWithTrafiData(vehicleProfileLogic) : renderWithoutTrafiData()}
                                {(vehicleProfileLogic.error || insufficientDataError) && (
                                    <p className="alert-text">{t('events:inspectionDialog.fetchError')}</p>
                                )}
                                <div className="action-wrapper action-wrapper--center action-wrapper--vertical">
                                    {renderActions(vehicleProfileLogic, eventLogic)}
                                </div>
                            </div>
                        )}
                    </VehicleProfileLogic>
                )}
            </EventLogic>
        </AppContext>
    )
}

InspectionEventFormReminderTrafiDialog.propTypes = {
    children: PropTypes.func.isRequired,
    wrappedVehicle: PropTypes.object.isRequired,
}

export default flowRight(
    withRouter,
    withTranslationCustom(['events', 'vehicleProfile']),
)(InspectionEventFormReminderTrafiDialog)
